/**
 * Renders one canvas (layout + photos + optional frame) to a single image Blob.
 * Used when completing edit to send final images to the Bridge (photo server).
 */

import type { CanvasLayout } from './types/canvas-layouts';
import { SLOT_GAP_PERCENT } from './types/canvas-layouts';
import type { PhotoTransform } from './types/edit-session';

/** Photo with at least id and imageBlob for rendering */
export interface PhotoForRender {
  photoId: string;
  imageBlob: Blob;
}

/** Frame with imageBlob for overlay */
export interface FrameForRender {
  imageBlob: Blob;
}

export interface RenderCanvasOptions {
  /** Output width in pixels (default 1200 for 4x6 at 200 DPI-ish) */
  width?: number;
  /** Output height in pixels (default 1800 for 4x6) */
  height?: number;
  /** JPEG quality 0-1 (default 0.95) */
  quality?: number;
  /** Per-slot pan/zoom (slot index -> PhotoTransform). Applied on top of cover-fit. */
  transforms?: Map<number, PhotoTransform>;
}

const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 1800;

function getPhotoById(photos: PhotoForRender[], photoId: string): PhotoForRender | null {
  return photos.find((p) => p.photoId === photoId) ?? null;
}

/**
 * Draw one canvas to an offscreen canvas and return as JPEG Blob.
 * @param layout - Canvas layout (slot geometry)
 * @param slotPhotoIds - Photo ID for each slot in layout order (length = layout.slots.length)
 * @param photos - All photos (must include entries for each slotPhotoId)
 * @param frame - Optional frame to overlay on the full canvas (one frame per canvas)
 * @param options - Output dimensions and quality
 */
export async function renderCanvasToBlob(
  layout: CanvasLayout,
  slotPhotoIds: string[],
  photos: PhotoForRender[],
  frame: FrameForRender | null,
  options: RenderCanvasOptions = {}
): Promise<Blob> {
  const width = options.width ?? DEFAULT_WIDTH;
  const height = options.height ?? DEFAULT_HEIGHT;
  const quality = options.quality ?? 0.95;
  const transforms = options.transforms;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2d context not available');
  }

  // White background so transparent/uncovered areas export as white (JPEG has no transparency)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  // Load frame image once if present
  let frameBitmap: ImageBitmap | null = null;
  if (frame) {
    frameBitmap = await createImageBitmap(frame.imageBlob);
  }

  for (let i = 0; i < layout.slots.length; i++) {
    const slot = layout.slots[i];
    const photoId = slotPhotoIds[i];
    if (!slot || !photoId) continue;

    const photo = getPhotoById(photos, photoId);
    if (!photo) continue;

    const half = SLOT_GAP_PERCENT / 2;
    const slotX = ((slot.x + half) / 100) * width;
    const slotY = ((slot.y + half) / 100) * height;
    const slotW = (Math.max(0, slot.width - SLOT_GAP_PERCENT) / 100) * width;
    const slotH = (Math.max(0, slot.height - SLOT_GAP_PERCENT) / 100) * height;

    const photoBitmap = await createImageBitmap(photo.imageBlob);

    const t = transforms?.get(i);
    const scaleMult = t?.scale ?? 1;
    const offsetX = (t?.offsetX ?? 0) * slotW;
    const offsetY = (t?.offsetY ?? 0) * slotH;
    const rotation = t?.rotation ?? 0;

    // Draw photo scaled to fill slot (cover), then apply user scale, pan, rotation
    const baseScale = Math.max(slotW / photoBitmap.width, slotH / photoBitmap.height);
    const scale = baseScale * scaleMult;
    const drawW = photoBitmap.width * scale;
    const drawH = photoBitmap.height * scale;
    const drawX = slotX + (slotW - drawW) / 2 + offsetX;
    const drawY = slotY + (slotH - drawH) / 2 + offsetY;

    ctx.save();
    // Fill slot with white so gaps around rotated/smaller photo export as white, not black
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(slotX, slotY, slotW, slotH);
    // Clip to slot bounds
    ctx.beginPath();
    ctx.rect(slotX, slotY, slotW, slotH);
    ctx.clip();

    if (rotation !== 0) {
      // Rotate around slot center
      const cx = slotX + slotW / 2;
      const cy = slotY + slotH / 2;
      ctx.translate(cx, cy);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-cx, -cy);
    }

    ctx.drawImage(photoBitmap, drawX, drawY, drawW, drawH);
    ctx.restore();
    photoBitmap.close();
  }

  // One frame applied to the entire canvas (not per slot)
  if (frameBitmap) {
    ctx.drawImage(frameBitmap, 0, 0, width, height);
    frameBitmap.close();
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob from canvas'));
      },
      'image/jpeg',
      quality
    );
  });
}
